import { useNavigate } from "../lib/router-context";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../components/LanguageSelector";
import { AccountLink } from "../components/AccountLink";
import { AdminLink } from "../components/AdminLink";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { TabButton, TabGroup } from "../components/ui/Tabs";
import { Leaderboard } from "../components/Leaderboard";
import { Timeline } from "../components/Timeline";
import { api, type ScoreEntry } from "../lib/api";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";

import { ThemeToggle } from "../components/ThemeToggle";
import { HomeLink } from "../components/HomeLink";
import { DiscordButton } from "../components/DiscordButton";
import { trackEvent } from "../lib/analytics";
import { useOpenLobbyStore } from "../lib/games/openlobby/store";
import { useOpenLobbySSE } from "../lib/useOpenLobbySSE";
import { getRequestedGameLanguage } from "../lib/wikipediaApi";
import { OpenLobbyCard } from "../components/OpenLobbyCard";
import { SvgIcon } from "../components/ui/SvgIcon";
import mapIcon from "../assets/map-icon.svg?raw";
import chevronRightIcon from "../assets/chevron-right-icon.svg?raw";
import { features } from "../lib/features";

interface LeaderboardDisplayEntry {
  name: string;
  time: number;
  clicks: number;
  meta?: string;
}

export function IndexPage() {
  const navigate = useNavigate();
  const [lobbyCode, setLobbyCode] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const activeDate = useAppStore((state) => state.activeDate);
  const setActiveDate = useAppStore((state) => state.setActiveDate);
  const [activeView, setActiveView] = useState<"leaderboard" | "paths">(
    "leaderboard",
  );
  const [leaderboard, setLeaderboard] = useState<LeaderboardDisplayEntry[]>([]);
  const [scoreboard, setScoreboard] = useState<ScoreEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [streamer, setStreamer] = useState<string | null>(null);
  const lang = useAppStore((state) => state.lang);
  const gameLanguage = getRequestedGameLanguage(lang);
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  const footerLinkClassName = `${theme.textMuted} ${isDark ? "hover:text-white" : "hover:text-gray-900"} transition-colors`;

  useEffect(() => {
    let isCancelled = false;

    api
      .getStreamer()
      .then((channel) => {
        if (!isCancelled) setStreamer(channel);
      })
      .catch(() => {
        if (!isCancelled) setStreamer(null);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  const { connected } = useOpenLobbySSE(gameLanguage, features.openLobby);
  const openLobbyState = useOpenLobbyStore((state) => state.gameState);
  const [gameRemaining, setGameRemaining] = useState<number | null>(null);
  const [scoreCountdown, setScoreCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!openLobbyState?.start_time) return;
    const startMs = new Date(openLobbyState.start_time).getTime();
    const scoreDuration = openLobbyState.score_duration;
    const gameEnd = startMs + openLobbyState.game_duration * 1000;
    const roundEnd = gameEnd + scoreDuration * 1000;

    const tick = () => {
      const now = Date.now();
      setGameRemaining(Math.max(0, Math.round((gameEnd - now) / 1000)));
      setScoreCountdown(Math.max(0, Math.round((roundEnd - now) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    openLobbyState?.start_time,
    openLobbyState?.game_duration,
    openLobbyState?.score_duration,
  ]);

  useEffect(() => {
    let isCancelled = false;
    const daysAgo = activeDate === "today" ? 0 : 1;

    void Promise.resolve().then(() => {
      if (!isCancelled) setIsLoading(true);
    });

    api
      .getScoreboard(gameLanguage, daysAgo)
      .then((data) => {
        if (isCancelled) return;

        setScoreboard(data.scores || []);
        const entries = (data.scores || [])
          .slice(0, 10)
          .map((s: ScoreEntry) => ({
            name: s.player_name,
            image: s.player_image,
            provider: s.player_provider,
            time: s.completion_time_seconds,
            clicks: s.moves.length,
            meta: `${s.found_count}/${s.target_count}`,
          }));
        setLeaderboard(entries);
      })
      .catch(() => {
        if (isCancelled) return;

        setScoreboard([]);
        setLeaderboard([]);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [gameLanguage, activeDate]);

  const handleCreateLobby = async () => {
    trackEvent("create-lobby-hero");
    setIsCreating(true);
    api
      .createLobby(gameLanguage)
      .then((id) => {
        setIsCreating(false);
        navigate(`/wikiparty/${id}`);
      })
      .catch((err) => {
        setCreateError(
          err instanceof Error ? err.message : t("home.errors.createLobby"),
        );
        setIsCreating(false);
      });
  };

  const handleJoinLobby = () => {
    if (lobbyCode) {
      const code = lobbyCode.match(/wikiparty\/(.+)/)?.[1] || lobbyCode;
      trackEvent("join-lobby-hero", { lobbyCode: code });
      navigate(`/wikiparty/${code}`);
    }
  };

  const handleDailyChallenge = () => {
    trackEvent("daily-challenge-hero");
    navigate("/daily");
  };

  const handleDuels = () => {
    trackEvent("duels-hero");
    navigate("/duels");
  };

  const timelinePlayers =
    scoreboard?.map((score) => ({
      name: score.player_name,
      articles: score.moves.map((move) => ({
        text: move.title,
        duration: move.duration_seconds,
        type: move.is_target ? ("highlight" as const) : undefined,
      })),
    })) || [];

  const twitchEmbedUrl = streamer
    ? `https://player.twitch.tv/?channel=${encodeURIComponent(streamer)}&parent=${encodeURIComponent(window.location.hostname)}&muted=true`
    : null;

  if (isCreating) {
    return (
      <div
        className={`min-h-screen ${theme.bg} flex items-center justify-center`}
      >
        <div className="relative">
          <div
            className={`w-12 h-12 rounded-full border-2 ${isDark ? "border-zinc-800 border-t-white" : "border-gray-300 border-t-gray-900"} animate-spin`}
          />
        </div>
      </div>
    );
  }

  if (createError) {
    return (
      <div
        className={`min-h-screen ${theme.bg} flex items-center justify-center p-6`}
      >
        <div className="text-center space-y-4">
          <div
            className={`w-16 h-16 rounded-2xl ${theme.errorBg} flex items-center justify-center mx-auto`}
          >
            <svg
              className={`w-8 h-8 ${theme.errorText}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <p className={`${theme.text} font-medium`}>
            {t("home.errors.createChallenge")}
          </p>
          <p className={`${theme.textMuted} text-sm`}>{createError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Header */}
      <header className="w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <HomeLink />
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <AdminLink />
              <AccountLink />
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="pt-8 pb-24">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {/* Hero Content */}
          <div className="text-center mb-24">
            <h1
              className={`text-6xl md:text-7xl font-light ${theme.text} tracking-tight mb-6`}
            >
              {t("home.hero.title")}
            </h1>

            <p className={`${theme.textMuted} text-lg max-w-xl mx-auto mb-12`}>
              {t("home.hero.subtitle")}
            </p>

            {/* Open Lobby Card - full width above the grid */}
            {features.openLobby && (
              <div className="mx-auto mb-12 max-w-4xl text-left">
                {openLobbyState ? (
                  <OpenLobbyCard
                    lobbyId={openLobbyState.id}
                    startTitle={openLobbyState.start.title}
                    targetTitle={openLobbyState.target_article.title}
                    phase={openLobbyState.phase}
                    gameRemaining={gameRemaining}
                    scoreCountdown={scoreCountdown}
                    isDark={isDark}
                    theme={theme}
                    playerCount={Object.keys(openLobbyState.players).length}
                  />
                ) : connected ? (
                  <div
                    className={`flex items-center justify-center py-4 rounded-xl ${theme.card} ${theme.cardBorder}`}
                  >
                    <span className={`text-sm ${theme.textMuted}`}>
                      {t("home.openLobby.waiting")}
                    </span>
                  </div>
                ) : (
                  <div
                    className={`flex items-center justify-center py-4 rounded-xl ${theme.card} ${theme.cardBorder}`}
                  >
                    <span className={`text-sm ${theme.textMuted}`}>
                      {t("home.openLobby.none")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action Cards */}
            <div
              className={`grid gap-4 mx-auto ${features.duels ? "max-w-4xl md:grid-cols-3" : "max-w-3xl md:grid-cols-2"}`}
            >
              {/* Daily Challenge Card */}
              <button
                onClick={handleDailyChallenge}
                className={`group relative text-left cursor-pointer flex flex-col ${theme.accentBg} rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${isDark ? "hover:shadow-white/10" : "hover:shadow-gray-900/10"}`}
              >
                <div className="flex w-full items-start justify-between mb-4">
                  <div
                    className={`w-10 h-10 rounded-lg ${theme.iconBg} flex items-center justify-center`}
                  >
                    <svg
                      className={`w-5 h-5 ${theme.iconText}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className={`inline-flex items-center gap-2`}>
                    <span
                      className={`w-2 h-2 rounded-full ${isDark ? "bg-white" : "bg-gray-900"} animate-pulse`}
                    />
                    <span className={`${theme.textMuted} text-sm`}>
                      {new Date().toLocaleDateString(lang, {
                        timeZone: "UTC",
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </div>
                <h3 className={`${theme.accentText} font-medium text-lg mb-1`}>
                  {t("home.dailyCard.title")}
                </h3>
                <p className={`${theme.textMuted} text-sm flex-1`}>
                  {t("home.dailyCard.description")}
                </p>
                <div
                  className={`mt-4 flex items-center gap-2 ${theme.accentText} font-medium text-sm`}
                >
                  {t("home.dailyCard.cta")}
                  <SvgIcon
                    markup={chevronRightIcon}
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </button>

              {features.duels && (
                <button
                  onClick={handleDuels}
                  className={`group text-left cursor-pointer flex flex-col ${theme.card} rounded-xl p-6 ${theme.cardBorder} transition-all duration-300 ${theme.cardHover}`}
                >
                  <div className="flex w-full items-start justify-between mb-4">
                    <div
                      className={`w-10 h-10 rounded-lg ${isDark ? "bg-zinc-800" : "bg-gray-100"} flex items-center justify-center`}
                    >
                      <svg
                        className={`w-5 h-5 ${theme.text}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 8l8 8m0-8l-8 8M7 4h10a3 3 0 013 3v10a3 3 0 01-3 3H7a3 3 0 01-3-3V7a3 3 0 013-3z"
                        />
                      </svg>
                    </div>
                    <span
                      className={`text-xs font-medium ${theme.textMuted} uppercase tracking-wider`}
                    >
                      {t("home.duelsCard.badge")}
                    </span>
                  </div>
                  <h3 className={`${theme.text} font-medium text-lg mb-1`}>
                    {t("home.duelsCard.title")}
                  </h3>
                  <p className={`${theme.textMuted} text-sm flex-1`}>
                    {t("home.duelsCard.description")}
                  </p>
                  <div
                    className={`mt-4 flex items-center gap-2 ${theme.text} font-medium text-sm`}
                  >
                    {t("home.duelsCard.cta")}
                    <SvgIcon
                      markup={chevronRightIcon}
                      size={16}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </button>
              )}

              {/* Multiplayer Card */}
              <button
                data-testid="create-lobby-btn"
                onClick={handleCreateLobby}
                className={`group text-left cursor-pointer flex flex-col ${theme.card} rounded-xl p-6 ${theme.cardBorder} transition-all duration-300 ${theme.cardHover}`}
              >
                <div className="flex w-full items-start justify-between mb-4">
                  <div
                    className={`w-10 h-10 rounded-lg ${isDark ? "bg-zinc-800" : "bg-gray-100"} flex items-center justify-center`}
                  >
                    <svg
                      className={`w-5 h-5 ${theme.text}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <span
                    className={`text-xs font-medium ${theme.textMuted} uppercase tracking-wider`}
                  >
                    {t("home.multiplayerCard.badge")}
                  </span>
                </div>
                <h3 className={`${theme.text} font-medium text-lg mb-1`}>
                  {t("home.multiplayerCard.title")}
                </h3>
                <p className={`${theme.textMuted} text-sm flex-1`}>
                  {t("home.multiplayerCard.description")}
                </p>
                <div
                  className={`mt-4 flex items-center gap-2 ${theme.text} font-medium text-sm`}
                >
                  {t("home.multiplayerCard.cta")}
                  <SvgIcon
                    markup={chevronRightIcon}
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </button>
            </div>

            {/* Join by Code */}
            <div className="mt-6 max-w-2xl mx-auto">
              <div
                className={`flex items-center gap-3 ${theme.bgSecondary} rounded-xl p-2 border ${theme.border}`}
              >
                <Input
                  placeholder={t("home.joinCode.placeholder")}
                  value={lobbyCode}
                  onChange={(e) => setLobbyCode(e.target.value)}
                  className={`flex-1 p-3 bg-transparent border-0 ${theme.inputText} ${theme.inputPlaceholder} focus:ring-0`}
                />
                <Button
                  onClick={handleJoinLobby}
                  disabled={!lobbyCode}
                  className={`px-6 py-2.5 ${theme.buttonPrimary} ${theme.buttonPrimaryText} rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  {t("home.joinCode.button")}
                </Button>
              </div>
            </div>

            <div className="mt-12 max-w-4xl mx-auto space-y-3">
              <h3
                className={`text-md font-medium tracking-widest uppercase ${theme.textMuted}`}
              >
                {t("home.stream.currentlyStreaming")}
              </h3>
              {twitchEmbedUrl ? (
                <div
                  className={`overflow-hidden rounded-xl border ${theme.border} ${theme.bgSecondary}`}
                >
                  <iframe
                    src={twitchEmbedUrl}
                    title={`${streamer} on Twitch`}
                    allowFullScreen
                    className="block w-full aspect-video"
                  />
                </div>
              ) : (
                <a
                  href="https://www.twitch.tv/directory/category/wikiparty-org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`rounded-xl border border-dashed ${theme.border} ${theme.bgSecondary} flex items-center justify-center py-12 cursor-pointer transition-colors ${theme.cardHover}`}
                >
                  <span className={`text-sm ${theme.textMuted}`}>
                    {t("home.stream.noStreamer")}
                  </span>
                </a>
              )}
            </div>
          </div>

          {/* Leaderboard Section */}
          <div className="max-w-3xl mx-auto">
            {/* Date Selector */}
            <div className="flex items-center justify-between mb-6">
              <h2 className={`${theme.text} text-xl font-medium`}>
                {t("home.leaderboard.title")}
              </h2>
              <TabGroup theme={theme}>
                <TabButton
                  active={activeDate === "today"}
                  onClick={() => setActiveDate("today")}
                  isDark={isDark}
                  theme={theme}
                >
                  {t("home.leaderboard.today")}
                </TabButton>
                <TabButton
                  active={activeDate === "yesterday"}
                  onClick={() => {
                    setActiveDate("yesterday");
                    setActiveView("leaderboard");
                  }}
                  isDark={isDark}
                  theme={theme}
                >
                  {t("home.leaderboard.yesterday")}
                </TabButton>
              </TabGroup>
            </div>

            {/* View Tabs (only for yesterday) */}
            {activeDate === "yesterday" && (
              <TabGroup theme={theme} className="mb-6">
                <TabButton
                  active={activeView === "leaderboard"}
                  onClick={() => setActiveView("leaderboard")}
                  isDark={isDark}
                  theme={theme}
                  className="flex-1"
                >
                  {t("home.leaderboard.tabs.leaderboard")}
                </TabButton>
                <TabButton
                  active={activeView === "paths"}
                  onClick={() => setActiveView("paths")}
                  isDark={isDark}
                  theme={theme}
                  className="flex-1"
                >
                  {t("home.leaderboard.tabs.paths")}
                </TabButton>
              </TabGroup>
            )}

            {/* Content */}
            {isLoading ? (
              <div className="flex justify-center py-16">
                <div
                  className={`w-8 h-8 rounded-full border-2 ${isDark ? "border-zinc-800 border-t-white" : "border-gray-300 border-t-gray-900"} animate-spin`}
                />
              </div>
            ) : activeDate === "yesterday" && activeView === "paths" ? (
              // Player Paths View (Yesterday only)
              <div
                className={`rounded-2xl ${theme.sectionBorder} ${theme.sectionBg} p-6`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.bgSecondary} ${theme.border} border`}
                  >
                    <SvgIcon
                      markup={mapIcon}
                      size={16}
                      className={`${theme.text}`}
                    />
                  </div>
                  <h3
                    className={`text-sm font-semibold uppercase tracking-wider ${theme.textMuted}`}
                  >
                    {t("home.leaderboard.howEveryonePlayed")}
                  </h3>
                </div>

                {timelinePlayers.length > 0 ? (
                  <Timeline players={timelinePlayers} isDark={isDark} />
                ) : (
                  <div className={`text-center py-16 ${theme.textMuted}`}>
                    <div
                      className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${theme.bgSecondary} flex items-center justify-center`}
                    >
                      <SvgIcon
                        markup={mapIcon}
                        size={32}
                        className={`${theme.textMuted}`}
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="text-sm">
                      {t("home.leaderboard.noPathData")}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Leaderboard View
              <div
                className={`rounded-2xl ${theme.sectionBorder} ${theme.sectionBg} overflow-hidden`}
              >
                <Leaderboard
                  entries={leaderboard}
                  isDark={isDark}
                  theme={theme}
                  maxItems={10}
                />
              </div>
            )}

            <p className={`text-center ${theme.textMuted} text-sm mt-4`}>
              {activeDate === "today"
                ? t("home.leaderboard.todaysResults")
                : t("home.leaderboard.yesterdaysResults")}
            </p>
          </div>

          {/* Game Modes Section */}
          <div className="max-w-4xl mx-auto mt-24">
            <h2
              className={`text-3xl font-light ${theme.text} tracking-tight text-center mb-12`}
            >
              {t("home.howToPlay.title")}
            </h2>
            <div
              className={`grid gap-6 ${features.duels ? "md:grid-cols-3" : "md:grid-cols-2"}`}
            >
              <div
                className={`rounded-xl p-6 ${theme.card} ${theme.cardBorder} ${theme.cardHover} transition-all duration-300`}
              >
                <h3 className={`${theme.text} font-medium text-lg mb-3`}>
                  {t("home.howToPlay.multiplayer.title")}
                </h3>
                <ol
                  className={`${theme.textMuted} text-sm space-y-2 list-decimal list-inside`}
                >
                  <li>{t("home.howToPlay.multiplayer.steps.0")}</li>
                  <li>{t("home.howToPlay.multiplayer.steps.1")}</li>
                  <li>{t("home.howToPlay.multiplayer.steps.2")}</li>
                  <li>{t("home.howToPlay.multiplayer.steps.3")}</li>
                  <li>{t("home.howToPlay.multiplayer.steps.4")}</li>
                  <li>{t("home.howToPlay.multiplayer.steps.5")}</li>
                </ol>
              </div>
              <div
                className={`rounded-xl p-6 ${theme.card} ${theme.cardBorder} ${theme.cardHover} transition-all duration-300`}
              >
                <h3 className={`${theme.text} font-medium text-lg mb-3`}>
                  {t("home.howToPlay.daily.title")}
                </h3>
                <ol
                  className={`${theme.textMuted} text-sm space-y-2 list-decimal list-inside`}
                >
                  <li>{t("home.howToPlay.daily.steps.0")}</li>
                  <li>{t("home.howToPlay.daily.steps.1")}</li>
                  <li>{t("home.howToPlay.daily.steps.2")}</li>
                  <li>{t("home.howToPlay.daily.steps.3")}</li>
                  <li>{t("home.howToPlay.daily.steps.4")}</li>
                  <li>{t("home.howToPlay.daily.steps.5")}</li>
                </ol>
              </div>
              {features.duels && (
                <div
                  className={`rounded-xl p-6 ${theme.card} ${theme.cardBorder} ${theme.cardHover} transition-all duration-300`}
                >
                  <h3 className={`${theme.text} font-medium text-lg mb-3`}>
                    {t("home.howToPlay.duels.title")}
                  </h3>
                  <ol
                    className={`${theme.textMuted} text-sm space-y-2 list-decimal list-inside`}
                  >
                    <li>{t("home.howToPlay.duels.steps.0")}</li>
                    <li>{t("home.howToPlay.duels.steps.1")}</li>
                    <li>{t("home.howToPlay.duels.steps.2")}</li>
                    <li>{t("home.howToPlay.duels.steps.3")}</li>
                    <li>{t("home.howToPlay.duels.steps.4")}</li>
                    <li>{t("home.howToPlay.duels.steps.5")}</li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className={`border-t ${theme.border} px-4 sm:px-6 lg:px-8 py-8`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p
              className={`${theme.textMuted} text-sm text-center sm:text-left`}
            >
              {t("home.footer.feedback")}{" "}
              <a
                href="mailto:info@wikiparty.org"
                className={`${theme.text} font-medium`}
              >
                info@wikiparty.org
              </a>{" "}
              {t("home.footer.orDiscord")}
            </p>
            <DiscordButton buttonClassName="w-auto px-4 py-2 text-sm rounded-lg" />
          </div>
          <nav
            aria-label="WikiParty pages"
            className={`flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs ${theme.textMuted} sm:justify-start`}
          >
            <a href="/wiki-race/" className={footerLinkClassName}>
              Wiki Race
            </a>
            <a href="/multiplayer/" className={footerLinkClassName}>
              Multiplayer
            </a>
            <a href="/daily-puzzle-game/" className={footerLinkClassName}>
              Daily Puzzle Game
            </a>
            <a
              href="/the-wiki-game-alternative/"
              className={footerLinkClassName}
            >
              The Wiki Game Alternative
            </a>
            <a href="/wikipedia-game/" className={footerLinkClassName}>
              Wikipedia Game
            </a>
            <a href="/wikipedia-speedrun/" className={footerLinkClassName}>
              Wikipedia Speedrun
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
